// ============================================================================
// FSA TEMPLATE HOOKS
// ============================================================================
// React hooks for the FSA Template browser (Stage 1 = read-only).
//
// - useFsaTemplates(params)        → paged list, refetch
// - useFsaTemplate(id)             → single header
// - useFsaTemplateHierarchy(id)    → full tree (View Modal)
//
// Stage 1 ships no mutations (no create / update / delete / clone hooks).

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fsaTemplateService } from 'api/services/fsa/fsaTemplateService';
import type { ID, PaginatedResponse } from 'api/types';
import type {
  FsTemplate,
  FsTemplateHierarchy,
  FsTemplateSearchRequest,
} from 'api/types/fsa/template.types';

const EMPTY_PAGINATION = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/** Paged list of FSA templates. Refetches when `params` changes. */
export const useFsaTemplates = (params?: FsTemplateSearchRequest) => {
  const [data, setData] = useState<FsTemplate[]>([]);
  const [pagination, setPagination] =
    useState<PaginatedResponse<FsTemplate>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fsaTemplateService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return { data, pagination, loading, error, refetch: fetchTemplates };
};

/** Single FSA template header by id. */
export const useFsaTemplate = (id: ID | null | undefined) => {
  const [data, setData] = useState<FsTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchTemplate = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fsaTemplateService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  return { data, loading, error, refetch: fetchTemplate };
};

/**
 * Full template hierarchy (template + reports + schedules + sections +
 * classes + narratives + formats). Backend assembles in one round trip;
 * View Modal consumes this directly.
 */
export const useFsaTemplateHierarchy = (id: ID | null | undefined) => {
  const [data, setData] = useState<FsTemplateHierarchy | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchHierarchy = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fsaTemplateService.getFullHierarchy(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchHierarchy();
  }, [fetchHierarchy]);

  return { data, loading, error, refetch: fetchHierarchy };
};
